import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Target, Eye, Heart, TrendingUp, Users, Shield, Sparkles, Lightbulb, Leaf, Award, Globe } from 'lucide-react'

const visionMission = [
  {
    icon: Eye,
    title: 'Our Vision',
    content: "To be India's most trusted partner for Government Technology and Infrastructure transformation, empowering institutions to serve citizens better through innovative, sustainable, and inclusive solutions.",
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    highlights: ['Trusted Partner', 'Government Technology', 'Innovation First'],
  },
  {
    icon: Target,
    title: 'Our Mission',
    content: "To deliver end-to-end technology solutions that Design, Develop, Deploy, Integrate, Maintain, and Transform institutions. We commit to excellence in every project, integrity in every interaction, and innovation in every solution.",
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
    highlights: ['End-to-End Solutions', 'Excellence Driven', 'Innovation Focused'],
  },
]

const values = [
  {
    icon: Heart,
    title: 'Customer First',
    description: "We put our clients' success at the center of everything we do.",
    color: '#ec4899',
  },
  {
    icon: Lightbulb,
    title: 'Continuous Innovation',
    description: 'We embrace change and continuously evolve with emerging technologies.',
    color: '#f59e0b',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We deliver quality that exceeds expectations, every single time.',
    color: '#6366f1',
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'We operate with transparency, honesty, and accountability.',
    color: '#10b981',
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'We believe in the power of partnerships and teamwork.',
    color: '#8b5cf6',
  },
  {
    icon: Leaf,
    title: 'Sustainability',
    description: 'We build solutions that are environmentally responsible and future-ready.',
    color: '#14b8a6',
  },
]

const stats = [
  { icon: Globe, value: 'Pan-India', label: 'Presence' },
  { icon: Award, value: '10', label: 'ISO Certifications' },
  { icon: Users, value: '50+', label: 'Team Members' },
  { icon: TrendingUp, value: '15+', label: 'Years Experience' },
]

export default function VisionMission() {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <>
      <Helmet>
        <title>Vision, Mission & Values | Eonixa Dynamics</title>
        <meta name="description" content="Our vision to be India's most trusted Government Technology partner. Our mission to transform institutions through innovation and excellence." />
      </Helmet>

      <section className="min-h-screen relative pt-20">
        {/* Background is global in App.tsx */}
        <div className="container-enterprise relative z-10 py-16">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
              }}
            >
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-sm text-slate-300">Our Foundation</span>
            </motion.div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Vision, Mission <span className="gradient-text">& Values</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Guided by a clear purpose and strong principles that drive every decision we make.
            </p>
          </motion.div>

          {/* Vision & Mission Cards */}
          <div ref={ref} className="grid gap-8 lg:grid-cols-2 mb-16">
            {visionMission.map((item, index) => {
              const Icon = item.icon
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="group relative"
                >
                  <div className="h-full rounded-3xl p-8 transition-all duration-300 hover:translate-y-[-4px]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    {/* Icon */}
                    <div className="mb-6">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                        style={{
                          background: item.gradient,
                          boxShadow: `0 0 30px ${item.color}40`,
                        }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-bold text-white mb-4 group-hover:text-primary transition-colors">
                      {item.title}
                    </h2>

                    {/* Content */}
                    <p className="text-slate-300 leading-relaxed mb-6">
                      {item.content}
                    </p>

                    {/* Highlights */}
                    <div className="flex flex-wrap gap-2">
                      {item.highlights.map((highlight) => (
                        <span
                          key={highlight}
                          className="px-3 py-1 text-xs font-medium rounded-full"
                          style={{
                            background: `${item.color}15`,
                            border: `1px solid ${item.color}30`,
                            color: item.color,
                          }}
                        >
                          {highlight}
                        </span>
                      ))}
                    </div>

                    {/* Bottom Accent */}
                    <div className="absolute bottom-0 left-8 right-8 h-1 rounded-t-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ background: item.gradient }}
                    />
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                  className="text-center p-6 rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                    }}
                  >
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </motion.div>
              )
            })}
          </motion.div>

          {/* Core Values */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Our Core Values</h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                The principles that guide our actions and define our culture
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {values.map((value, index) => {
                const Icon = value.icon
                return (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.3, delay: 0.1 + index * 0.1 }}
                    className="group relative"
                  >
                    <div className="p-6 rounded-2xl h-full transition-all duration-300 hover:translate-y-[-2px]"
                      style={{
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(139, 92, 246, 0.04) 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                      }}
                    >
                      {/* Icon */}
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                        style={{
                          background: `${value.color}15`,
                          border: `1px solid ${value.color}30`,
                        }}
                      >
                        <Icon className="w-6 h-6" style={{ color: value.color }} />
                      </div>

                      {/* Title */}
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                        {value.title}
                      </h3>

                      {/* Description */}
                      <p className="text-sm text-slate-400">{value.description}</p>

                      {/* Bottom Accent */}
                      <div className="absolute bottom-0 left-6 right-6 h-1 rounded-t-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ background: `linear-gradient(90deg, transparent, ${value.color}, transparent)` }}
                      />
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center gap-4 px-8 py-4 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
              }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
              >
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-white">Driven by Purpose</p>
                <p className="text-xs text-slate-400">Building a better tomorrow, today</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}